import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  Assessment as MonitoringIcon
} from "@mui/icons-material";
import { useTheme } from "../../theme/ThemeProvider";
import { DarkMode, LightMode } from "@mui/icons-material";
import { useRouteLoading } from "../../hooks/useRouteLoading";
import { apiErrorBus } from "../../utils/apiErrorBus";

const logoPath = `${process.env.PUBLIC_URL}/bitnet-logo-high-res.png`;

const OrgAdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const routeLoading = useRouteLoading();
  const [apiErrorMessage, setApiErrorMessage] = useState(null);

  // Subscribe to global API errors
  useEffect(() => {
    const unsubscribe = apiErrorBus.subscribe((message) => {
      setApiErrorMessage(message);
    });
    return unsubscribe;
  }, []);

  // Determine active tab based on current route
  const currentTab = location.pathname === '/monitoring' ? 1 : 0;

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      navigate("/clients");
    } else if (newValue === 1) {
      navigate("/monitoring");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Route loading indicator */}
      {routeLoading && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
          }}
        />
      )}
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#0c2d48', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <img
              src={logoPath}
              alt="BitNet Logo"
              style={{
                maxHeight: 36,
                marginRight: 16,
                filter: isDarkMode ? 'brightness(0.9)' : 'none'
              }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                BitNet Security Platform
              </Typography>
              <Typography variant="caption" color="inherit" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                Organization Admin Portal
              </Typography>
            </Box>
          </Box>

          {/* Theme Toggle */}
          <IconButton onClick={toggleTheme} color="inherit" size="small" sx={{ mr: 1 }}>
            {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>

          {/* Logout Button */}
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            size="small"
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Logout
          </Button>
        </Toolbar>

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              px: 3,
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.85rem'
              }
            }}
          >
            <Tab
              icon={<BusinessIcon fontSize="small" />}
              iconPosition="start"
              label="Client Management"
            />
            <Tab
              icon={<MonitoringIcon fontSize="small" />}
              iconPosition="start"
              label="Client Monitoring"
            />
          </Tabs>
        </Box>
      </AppBar>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: (theme) => theme.palette.background.default,
          minHeight: "calc(100vh - 108px)",
        }}
      >
        <Outlet />
      </Box>

      {/* API Error Toast */}
      <Snackbar
        open={!!apiErrorMessage}
        autoHideDuration={4000}
        onClose={() => setApiErrorMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setApiErrorMessage(null)} severity="error">
          {apiErrorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrgAdminLayout;
